import React, { useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MapContainer, TileLayer, Marker, Circle, Polygon, Polyline, Tooltip, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import { UsersIcon, TargetIcon, MapPinIcon, FilterIcon, CheckIcon, SearchIcon, Trash2Icon, BookOpenIcon, CalendarIcon, HomeIcon, ChevronDownIcon } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { AGE_BRACKETS, DEPARTMENTS, BROSSARD_ADDRESSES, BROSSARD_CENTER, RADIUS_OPTIONS, RESIDENCE_OPTIONS, SECTORS, type AddressEntry, type Coord } from '../../data/communications';
import type { AudienceState } from '../../types/composer';
import type { AudienceKind } from '../../types/notice';
const mapMarkerIcon = L.divIcon({
  className: '',
  html: `<div style="width:20px;height:20px;border-radius:9999px;background:#4F46E5;border:3px solid white;box-shadow:0 0 0 3px rgba(79,70,229,0.3);"></div>`,
  iconSize: [20, 20],
  iconAnchor: [10, 10]
});

/** Captures map clicks while the user traces a custom polygon. */
function DrawLayer({
  active,
  points,
  onAddPoint
}: {
  active: boolean;
  points: Coord[];
  onAddPoint: (c: Coord) => void;
}) {
  useMapEvents({
    click(e) {
      if (active) onAddPoint([e.latlng.lat, e.latlng.lng]);
    }
  });
  if (points.length === 0) return null;
  return <>
      {points.length >= 3 && <Polygon positions={points} pathOptions={{
      color: '#4F46E5',
      fillColor: '#4F46E5',
      fillOpacity: 0.15
    }} data-id="element-3736" data-mp="3tvek6mywt3wy" />}
      {points.length === 2 && <Polyline positions={points} pathOptions={{
      color: '#4F46E5'
    }} data-id="element-3737" data-mp="6rgsdp7uuj75" />}
      {points.map((p, i) => <Circle key={i} center={p} radius={12} pathOptions={{
      color: '#4F46E5',
      fillColor: '#fff',
      fillOpacity: 1
    }} data-id="element-3738" data-mp="bll22g8i95bx" data-mpi={i} />)}
    </>;
}
interface AudienceSelectorProps {
  audience: AudienceState;
  onChange: (patch: Partial<AudienceState>) => void;
  audienceTotal: number;
  showSuggestions: boolean;
  onShowSuggestions: (v: boolean) => void;
}
export function AudienceSelector({
  audience,
  onChange,
  audienceTotal,
  showSuggestions,
  onShowSuggestions
}: AudienceSelectorProps) {
  const {
    t,
    lang
  } = useLanguage();
  const locale = lang === 'fr' ? 'fr-CA' : 'en-CA';
  const addressBoxRef = useRef<HTMLDivElement>(null);

  // Clicking (or tabbing) anywhere outside the address field closes its list.
  useEffect(() => {
    if (!showSuggestions) return;
    const handlePointerDown = (event: MouseEvent | TouchEvent) => {
      if (!addressBoxRef.current?.contains(event.target as Node)) {
        onShowSuggestions(false);
      }
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onShowSuggestions(false);
    };
    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('touchstart', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('touchstart', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [showSuggestions, onShowSuggestions]);
  const {
    zone,
    selectedSectors,
    locationSearch,
    locationRadius,
    addrCoords,
    drawnPoints,
    isDrawing,
    selectedDepartments,
    selectedAges,
    residence
  } = audience;
  const addressMatches = locationSearch ? BROSSARD_ADDRESSES.filter(a => a.label.toLowerCase().includes(locationSearch.toLowerCase())) : BROSSARD_ADDRESSES;
  const selectAddress = (entry: AddressEntry) => {
    onChange({
      locationSearch: entry.label,
      addrCoords: entry.coord
    });
    onShowSuggestions(false);
  };

  /** Adding/removing a district — shared by the checkbox list and the map. */
  const toggleSector = (id: string) => onChange({
    selectedSectors: selectedSectors.includes(id) ? selectedSectors.filter(s => s !== id) : [...selectedSectors, id]
  });
  const zones: {
    id: AudienceKind;
    icon: typeof UsersIcon;
    label: string;
  }[] = [{
    id: 'org',
    icon: UsersIcon,
    label: t("Toute l'organisation", 'Entire organization')
  }, {
    id: 'sector',
    icon: TargetIcon,
    label: t('Secteur spécifique', 'Specific district')
  }, {
    id: 'location',
    icon: MapPinIcon,
    label: t("Autour d'un lieu", 'Around a location')
  }, {
    id: 'filter',
    icon: FilterIcon,
    label: t('Par filtre', 'By filter')
  }];
  return <section className="space-y-6" data-id="element-3739" data-mp="1j4x3b7ivfxr9">
      <h2 className="text-xl font-semibold text-slate-900" data-id="element-3740" data-mp="1j45fwjzfh6c2">
        {t('Audience', 'Audience')}
      </h2>
      <div className="bg-white rounded-xl border-[0.5px] border-slate-200 p-6 space-y-6" data-id="element-3741" data-mp="1e5u6ubgyizuw">
        <div className="flex flex-wrap gap-3" data-id="element-3742" data-mp="2bmj4ru2sdhz0">
          {zones.map(z => {
          const Icon = z.icon;
          const active = zone === z.id;
          return <button key={z.id} onClick={() => onChange({
            zone: z.id
          })} className={`flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-medium transition-colors border ${active ? 'bg-indigo-50 border-[#4F46E5] text-[#4F46E5]' : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'}`} data-id="element-3743" data-mp="2e6z3s3ray5s4">
                <Icon className="w-4 h-4" data-id="element-3744" data-mp="2x2yy3l7r5lev" /> {z.label}
              </button>;
        })}
        </div>

        <AnimatePresence mode="wait" data-id="element-3745" data-mp="321s12jpexu0s">
          {zone === 'sector' && <motion.div key="sector" initial={{
          height: 0,
          opacity: 0
        }} animate={{
          height: 'auto',
          opacity: 1
        }} exit={{
          height: 0,
          opacity: 0
        }} transition={{
          duration: 0.2
        }} className="overflow-hidden" data-id="element-3746" data-mp="36vmsk9b9fdaf">
              <div className="pt-2" data-id="element-3747" data-mp="3kuw06klegz2s">
                <div className="flex items-center justify-between mb-3" data-id="element-3748" data-mp="1lpmdjb16n1y3">
                  <label className="block text-sm font-medium text-slate-700" data-id="element-3749" data-mp="1lni0o3lb2slo">
                    {t('Sélectionnez les secteurs à contacter', 'Select the districts to contact')}
                  </label>
                  <button type="button" onClick={() => onChange({
                selectedSectors: selectedSectors.length === SECTORS.length ? [] : SECTORS.map(s => s.id)
              })} className="text-xs font-semibold text-[#4F46E5] hover:text-[#4338CA]" data-id="element-3750" data-mp="1ppwm8vgd06gn">
                    {selectedSectors.length === SECTORS.length ? t('Tout désélectionner', 'Deselect all') : t('Tout sélectionner', 'Select all')}
                  </button>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-3751" data-mp="4zligbrjgxvm">
                  {SECTORS.map(s => {
                const checked = selectedSectors.includes(s.id);
                return <label key={s.id} className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${checked ? 'border-[#4F46E5] bg-indigo-50/30' : 'border-slate-200 hover:bg-slate-50'}`} data-id="element-3752" data-mp="6shlyz6y9ot">
                        <div className={`w-5 h-5 rounded border flex items-center justify-center flex-shrink-0 ${checked ? 'bg-[#4F46E5] border-[#4F46E5]' : 'border-slate-300 bg-white'}`} data-id="element-3753" data-mp="3ddapzu946k5j">
                          {checked && <CheckIcon className="w-3.5 h-3.5 text-white" data-id="element-3754" data-mp="2k5l8lsp02l8m" />}
                        </div>
                        <div className="min-w-0 flex-1" data-id="element-3755" data-mp="2f81tkqkha4fm">
                          <span className="block text-sm font-medium text-slate-700 truncate" data-id="element-3756" data-mp="2afs2363pswb8">
                            {s.label[lang]}
                          </span>
                          <span className="block text-xs text-slate-400" data-id="element-3757" data-mp="21flprnjr62dn">
                            {s.population.toLocaleString(locale)}{' '}
                            {t('résidents', 'residents')}
                          </span>
                        </div>
                        <input type="checkbox" className="hidden" checked={checked} onChange={() => toggleSector(s.id)} data-id="element-3758" data-mp="1rhz7cr38vsox" />
                      </label>;
              })}
                </div>

                <p className="mt-3 text-sm text-slate-500" data-id="element-3759" data-mp="13mm54omfmqmi">
                  {selectedSectors.length}{' '}
                  {t('secteur(s)', 'district(s)')} ·{' '}
                  <span className="font-semibold text-slate-700" data-id="element-3760" data-mp="18jcg4xjdqw12">
                    {audienceTotal.toLocaleString(locale)}
                  </span>{' '}
                  {t('résidents ciblés', 'residents targeted')}
                </p>

                {/* Map preview — clicking a district toggles it in the list */}
                <div className="mt-4 h-64 rounded-xl overflow-hidden border border-slate-200 relative" data-id="element-3761" data-mp="pmkystneeqzn">
                  <div className="absolute top-2 left-1/2 -translate-x-1/2 z-[400] pointer-events-none" data-id="element-3762" data-mp="tog03y5bx4df">
                    <span className="bg-white/90 backdrop-blur-sm px-4 py-1.5 rounded-full shadow-sm border border-slate-200 text-xs font-medium text-slate-500" data-id="element-3763" data-mp="ynsmyiw7abg0">
                      {t('Cliquez un secteur sur la carte pour le sélectionner', 'Click a district on the map to select it')}
                    </span>
                  </div>
                  <MapContainer center={BROSSARD_CENTER} zoom={13} scrollWheelZoom={false} style={{
                height: '100%',
                width: '100%'
              }} data-id="element-3764" data-mp="1bdc0zxc60ng5">
                    <TileLayer attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" data-id="element-3765" data-mp="1l3dlkf5b7nqb" />
                    {SECTORS.map(s => {
                  const selected = selectedSectors.includes(s.id);
                  return <Polygon key={s.id} positions={s.polygon} eventHandlers={{
                    click: () => toggleSector(s.id)
                  }} pathOptions={{
                    color: selected ? s.color : '#94A3B8',
                    weight: selected ? 2 : 1,
                    fillColor: selected ? s.color : '#CBD5E1',
                    fillOpacity: selected ? 0.3 : 0.05,
                    dashArray: selected ? undefined : '4 4',
                    className: 'cursor-pointer'
                  }} data-id="element-3766" data-mp="11gpjnij1seiv">
                          <Tooltip direction="center" permanent={selected} data-id="element-3767" data-mp="3emw3ss15ain5">
                            {s.label[lang]}
                          </Tooltip>
                        </Polygon>;
                })}
                  </MapContainer>
                </div>
              </div>
            </motion.div>}

          {zone === 'location' && <motion.div key="location" initial={{
          height: 0,
          opacity: 0
        }} animate={{
          height: 'auto',
          opacity: 1
        }} exit={{
          height: 0,
          opacity: 0
        }} transition={{
          duration: 0.2
        }} className="overflow-hidden" data-id="element-3768" data-mp="13ub2jqg81jzn">
              <div className="pt-2 space-y-4" data-id="element-3769" data-mp="1mrczlvhoozq6">
                <div className="flex flex-col sm:flex-row gap-4" data-id="element-3770" data-mp="1ms4iq6u3h4zk">
                  <div className="flex-1" data-id="element-3771" data-mp="190hmg705jwgs">
                    <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-3772" data-mp="18xbyiv39f0mn">
                      {t('Adresse centrale', 'Centre address')}
                    </label>
                    <div className="relative" ref={addressBoxRef} data-id="element-3773" data-mp="21olsitmmybk0">
                      <input type="text" value={locationSearch} onChange={e => {
                    onChange({
                      locationSearch: e.target.value
                    });
                    onShowSuggestions(true);
                  }} onFocus={() => onShowSuggestions(true)} placeholder={t('Rechercher une adresse à Brossard…', 'Search for an address in Brossard…')} aria-label={t('Rechercher une adresse', 'Search for an address')} className="w-full border border-slate-200 rounded-lg pl-10 pr-4 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[#4F46E5] focus:border-[#4F46E5]" data-id="element-3774" data-mp="25pz0pad7aqu3" />
                      <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" data-id="element-3775" data-mp="hptywxcrt60q" />
                      {showSuggestions && addressMatches.length > 0 && <ul className="absolute z-[1100] mt-1 w-full bg-white border border-slate-200 rounded-lg shadow-lg max-h-52 overflow-y-auto py-1" data-id="element-3776" data-mp="3m7zdvqawlqgx">
                          {addressMatches.map(a => <li key={a.label} data-id="element-3777" data-mp="3v4zydluur4f3">
                              <button type="button" onClick={() => selectAddress(a)} className="w-full flex items-center gap-2 text-left px-3 py-2 text-sm text-slate-700 hover:bg-indigo-50" data-id="element-3778" data-mp="37ggtlgb0h9rr">
                                <MapPinIcon className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" data-id="element-3779" data-mp="3l4qscbtja689" />
                                {a.label}
                              </button>
                            </li>)}
                        </ul>}
                    </div>
                  </div>
                  <div className="w-full sm:w-48" data-id="element-3780" data-mp="2scbhoy4o7tp">
                    <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-3781" data-mp="7nrka2ha3dj9">
                      {t('Rayon', 'Radius')}
                    </label>
                    <div className="relative" data-id="element-3782" data-mp="hmywung6v45b">
                      <select value={locationRadius} onChange={e => onChange({
                    locationRadius: e.target.value
                  })} disabled={drawnPoints.length >= 3} className="w-full border border-slate-200 rounded-lg pl-3 pr-10 py-2 text-sm appearance-none focus:outline-none focus:ring-1 focus:ring-[#4F46E5] focus:border-[#4F46E5] disabled:bg-slate-50 disabled:text-slate-400" data-id="element-3783" data-mp="hmqiss8bxssj">
                        {RADIUS_OPTIONS.map(r => <option key={r.value} value={r.value} data-id="element-3784" data-mp="ms2qmyamfi7l">
                            {r.label[lang]}
                          </option>)}
                      </select>
                      <ChevronDownIcon className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" data-id="element-3785" data-mp="1fgrsizji3rke" />
                    </div>
                  </div>
                </div>

                <div className="flex items-center justify-between flex-wrap gap-2" data-id="element-3786" data-mp="15iu5ughai2tw">
                  <span className="text-sm font-medium text-slate-700" data-id="element-3787" data-mp="15kybb2uu79wa">
                    {t('Ou dessiner une zone personnalisée', 'Or draw a custom area')}
                  </span>
                  <div className="flex items-center gap-2" data-id="element-3788" data-mp="3rr9sptoeyw10">
                    {drawnPoints.length > 0 && <button type="button" onClick={() => onChange({
                  drawnPoints: [],
                  isDrawing: false
                })} className="flex items-center gap-1.5 text-sm font-medium px-3 py-1.5 rounded-md bg-slate-100 text-slate-600 hover:bg-slate-200 transition-colors" data-id="element-3789" data-mp="j5f61m40gtr">
                        <Trash2Icon className="w-4 h-4" data-id="element-3790" data-mp="3dpxf3owq76ia" /> {t('Effacer', 'Clear')}
                      </button>}
                    <button type="button" onClick={() => isDrawing ? onChange({
                  isDrawing: false
                }) : onChange({
                  drawnPoints: [],
                  isDrawing: true
                })} className={`text-sm font-medium px-3 py-1.5 rounded-md transition-colors ${isDrawing ? 'bg-indigo-100 text-[#4F46E5]' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`} data-id="element-3791" data-mp="3003ob11nuc5x">
                      {isDrawing ? t('Terminer le tracé', 'Finish drawing') : t('Dessiner sur la carte', 'Draw on the map')}
                    </button>
                  </div>
                </div>

                <div className="h-64 rounded-xl overflow-hidden border border-slate-200 mt-2 relative" data-id="element-3792" data-mp="2pdov6cz44rex">
                  {isDrawing && <div className="absolute top-2 left-1/2 -translate-x-1/2 z-[400] bg-white/90 backdrop-blur-sm px-4 py-2 rounded-full shadow-sm border border-slate-200 text-xs font-medium text-[#4F46E5]" data-id="element-3793" data-mp="341jrs4g8rfxa">
                      {t('Cliquez sur la carte pour ajouter des points', 'Click the map to add points')}
                    </div>}
                  <MapContainer center={addrCoords ?? BROSSARD_CENTER} zoom={13} scrollWheelZoom={false} style={{
                height: '100%',
                width: '100%'
              }} data-id="element-3794" data-mp="jdca5wqzpuov">
                    <TileLayer attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" data-id="element-3795" data-mp="xbamkwlr3h1q" />
                    {addrCoords && drawnPoints.length < 3 && !isDrawing && <>
                        <Marker position={addrCoords} icon={mapMarkerIcon} data-id="element-3796" data-mp="9jbwv7lk1j52" />
                        <Circle center={addrCoords} radius={parseInt(locationRadius)} pathOptions={{
                    color: '#4F46E5',
                    fillColor: '#4F46E5',
                    fillOpacity: 0.12
                  }} data-id="element-3797" data-mp="ecy94ynt3l0c" />
                      </>}
                    <DrawLayer active={isDrawing} points={drawnPoints} onAddPoint={c => onChange({
                  drawnPoints: [...drawnPoints, c]
                })} data-id="element-3798" data-mp="35gjipcw5pwwl" />
                  </MapContainer>
                </div>

                {audienceTotal > 0 && <p className="text-sm text-slate-500" data-id="element-3799" data-mp="2ms4u0w9bauyy">
                    {drawnPoints.length >= 3 ? t('Zone personnalisée', 'Custom area') : t("Zone autour de l'adresse", 'Area around the address')}{' '}
                    · {t('environ', 'about')}{' '}
                    <span className="font-semibold text-slate-700" data-id="element-3800" data-mp="28sdg0vispeky">
                      {audienceTotal.toLocaleString(locale)}
                    </span>{' '}
                    {t('résidents', 'residents')}
                  </p>}
              </div>
            </motion.div>}

          {zone === 'filter' && <motion.div key="filter" initial={{
          height: 0,
          opacity: 0
        }} animate={{
          height: 'auto',
          opacity: 1
        }} exit={{
          height: 0,
          opacity: 0
        }} transition={{
          duration: 0.2
        }} className="overflow-hidden" data-id="element-3801" data-mp="1xn5qfn0pql13">
              <div className="pt-2 space-y-6" data-id="element-3802" data-mp="1jmuxmefuewda">
                {/* Departments */}
                <div data-id="element-3803" data-mp="1eublllwt09al">
                  <div className="flex items-center gap-2 mb-3" data-id="element-3804" data-mp="1ets426b2fmx3">
                    <BookOpenIcon className="w-4 h-4 text-[#4F46E5]" data-id="element-3805" data-mp="19vgt1s91b6da" />
                    <label className="block text-sm font-medium text-slate-700" data-id="element-3806" data-mp="14x6xvhnb85ro">
                      {t('Département de la ville', 'City department')}
                    </label>
                  </div>
                  <div className="flex flex-wrap gap-2" data-id="element-3807" data-mp="vwh74scwp5kt">
                    {DEPARTMENTS.map(d => {
                  const active = selectedDepartments.includes(d.id);
                  return <button key={d.id} type="button" onClick={() => onChange({
                    selectedDepartments: active ? selectedDepartments.filter(x => x !== d.id) : [...selectedDepartments, d.id]
                  })} className={`flex items-center gap-1.5 px-3.5 py-2 rounded-full text-sm font-medium border transition-colors ${active ? 'bg-indigo-50 border-[#4F46E5] text-[#4F46E5]' : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'}`} data-id="element-3808" data-mp="3bkwf08kbyjka">
                          {active && <CheckIcon className="w-3.5 h-3.5" data-id="element-3809" data-mp="2a2dhkl01l3hl" />}
                          {d.label[lang]}
                        </button>;
                })}
                  </div>
                  <p className="mt-2 text-xs text-slate-400" data-id="element-3810" data-mp="1y0ciazuh78rr">
                    {t('Laissez vide pour cibler tous les départements.', 'Leave empty to target every department.')}
                  </p>
                </div>

                {/* Age */}
                <div data-id="element-3811" data-mp="1f3kqg20vblc3">
                  <div className="flex items-center gap-2 mb-3" data-id="element-3812" data-mp="1f42yrx0t1q0d">
                    <CalendarIcon className="w-4 h-4 text-[#4F46E5]" data-id="element-3813" data-mp="1t32jwup3zmrs" />
                    <label className="block text-sm font-medium text-slate-700" data-id="element-3814" data-mp="1t4dqf29xv8ml">
                      {t("Tranche d'âge", 'Age bracket')}
                    </label>
                  </div>
                  <div className="flex flex-wrap gap-2" data-id="element-3815" data-mp="34go92fuyxodb">
                    {AGE_BRACKETS.map(a => {
                  const active = selectedAges.includes(a.id);
                  return <button key={a.id} type="button" onClick={() => onChange({
                    selectedAges: active ? selectedAges.filter(x => x !== a.id) : [...selectedAges, a.id]
                  })} className={`flex items-center gap-1.5 px-3.5 py-2 rounded-full text-sm font-medium border transition-colors ${active ? 'bg-indigo-50 border-[#4F46E5] text-[#4F46E5]' : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'}`} data-id="element-3816" data-mp="la2a3t78n0oj">
                          {active && <CheckIcon className="w-3.5 h-3.5" data-id="element-3817" data-mp="1sooz072b9toy" />}
                          {a.label[lang]}
                        </button>;
                })}
                  </div>
                  <p className="mt-2 text-xs text-slate-400" data-id="element-3818" data-mp="3v8xg34ff4ba2">
                    {t('Laissez vide pour cibler tous les âges.', 'Leave empty to target every age.')}
                  </p>
                </div>

                {/* Residence */}
                <div data-id="element-3819" data-mp="mtviaztl531v">
                  <div className="flex items-center gap-2 mb-3" data-id="element-3820" data-mp="msc4dfvej439">
                    <HomeIcon className="w-4 h-4 text-[#4F46E5]" data-id="element-3821" data-mp="irifaj683j8s" />
                    <label className="block text-sm font-medium text-slate-700" data-id="element-3822" data-mp="iuecu6e5s5wp">
                      {t('Type de résidence', 'Residence type')}
                    </label>
                  </div>
                  <div className="inline-flex rounded-lg border border-slate-200 bg-slate-50 p-1" data-id="element-3823" data-mp="90ebmflzzkt9">
                    {RESIDENCE_OPTIONS.map(r => {
                  const active = residence === r.value;
                  return <button key={r.value} type="button" onClick={() => onChange({
                    residence: r.value
                  })} className={`px-4 py-1.5 rounded-md text-sm font-medium transition-colors ${active ? 'bg-white text-[#4F46E5] shadow-sm' : 'text-slate-500 hover:text-slate-700'}`} data-id="element-3824" data-mp="192uae2689oey">
                          {r.label[lang]}
                        </button>;
                })}
                  </div>
                </div>

                {/* Summary */}
                <div className="rounded-lg bg-indigo-50/60 border border-indigo-100 px-4 py-3" data-id="element-3825" data-mp="26ohtxmhl4c92">
                  <p className="text-sm text-slate-600" data-id="element-3826" data-mp="2aspklp7ls29x">
                    {t('Audience filtrée', 'Filtered audience')} ·{' '}
                    {t('environ', 'about')}{' '}
                    <span className="font-semibold text-[#4F46E5]" data-id="element-3827" data-mp="fnggabdlv0hk">
                      {audienceTotal.toLocaleString(locale)}
                    </span>{' '}
                    {t('citoyens', 'citizens')}
                  </p>
                  <p className="mt-1 text-xs text-slate-400" data-id="element-3828" data-mp="pfm6bfkar7u4">
                    {selectedDepartments.length > 0 ? `${selectedDepartments.length} ${t('département(s)', 'department(s)')}` : t('Tous les départements', 'All departments')}{' '}
                    ·{' '}
                    {selectedAges.length > 0 ? AGE_BRACKETS.filter(a => selectedAges.includes(a.id)).map(a => a.label[lang]).join(', ') : t('Tous les âges', 'All ages')}{' '}
                    ·{' '}
                    {RESIDENCE_OPTIONS.find(r => r.value === residence)?.label[lang] ?? t('Tous', 'All')}
                  </p>
                </div>
              </div>
            </motion.div>}
        </AnimatePresence>
      </div>
    </section>;
}